import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, LayoutDashboard, Archive, ChevronDown, ChevronRight, Globe, ImageIcon, Bell } from 'lucide-react';
import { translateStatus } from '../i18n/translations';
import { StatusFlowBar } from '../components/StatusFlowBar';

const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

export const ManagerDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);
  const [notifBanner, setNotifBanner] = useState(null);
  const [alertNotifs, setAlertNotifs] = useState([]);
  const prevOrderCount = React.useRef(null);
  const prevAlertKeys = React.useRef(null);

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(true), 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const fetchData = async (isPolling = false) => {
    try {
      const [ordersRes, archivesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/manager/orders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/manager/archives`, { withCredentials: true })
      ]);
      const newOrders = ordersRes.data;
      if (isPolling && prevOrderCount.current !== null && newOrders.length > prevOrderCount.current) {
        const diff = newOrders.length - prevOrderCount.current;
        const msg = diff === 1 ? t('new_order_notification') : `${diff} ${t('new_orders_notification')}`;
        setNotifBanner(msg);
        setTimeout(() => setNotifBanner(null), 8000);
      }
      prevOrderCount.current = newOrders.length;
      setOrders(newOrders);

      // Detect orange/red alerts
      if (isPolling) {
        const currentColorKeys = new Set(newOrders.filter(o => o.color === 'orange' || o.color === 'red').map(o => `${o.order_id}-${o.color}`));
        const prevKeys = prevAlertKeys.current || new Set();
        const newColorAlerts = [...currentColorKeys].filter(k => !prevKeys.has(k));
        if (newColorAlerts.length > 0) {
          const hasRed = newColorAlerts.some(k => k.endsWith('-red'));
          const msg = hasRed ? t('alert_red_notification') : t('alert_orange_notification');
          setAlertNotifs(prev => [...prev, { id: Date.now(), msg }]);
          setTimeout(() => setAlertNotifs([]), 8000);
        }
        prevAlertKeys.current = currentColorKeys;
      } else {
        prevAlertKeys.current = new Set(newOrders.filter(o => o.color === 'orange' || o.color === 'red').map(o => `${o.order_id}-${o.color}`));
      }

      setArchives(archivesRes.data);
    } catch (error) {
      console.error('Error fetching manager data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/language`, { language: lang }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getRowBgColor = (color) => {
    switch (color) {
      case 'green': return 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100';
      case 'orange': return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
      case 'red': return 'bg-red-50 border-red-200 hover:bg-red-100';
      default: return 'bg-slate-50 border-slate-200 hover:bg-slate-100';
    }
  };

  const groupByMonth = (list) => {
    const groups = {};
    for (const o of list) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const toggleMonth = (label) => {
    setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const openImagePopup = (images) => {
    setImagePopupImages(images);
    setImagePopupOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col fixed inset-y-0">
          <div className="p-6 border-b border-slate-200">
            <img src={LOGO_URL} alt="Panther" className="h-10 object-contain" data-testid="sidebar-logo" />
            <p className="text-xs text-slate-600 mt-2">{user?.name}</p>
            <p className="text-xs text-slate-500">Manager</p>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            <button
              data-testid="manager-nav-orders"
              onClick={() => setActiveTab('orders')}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors ${activeTab === 'orders' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <LayoutDashboard size={18} />
              {t('orders_in_progress')}
            </button>
            <button
              data-testid="manager-nav-archives"
              onClick={() => setActiveTab('archives')}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors ${activeTab === 'archives' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Archive size={18} />
              {t('archives')}
            </button>
          </nav>
          <div className="p-4 border-t border-slate-200 space-y-2">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-slate-500" />
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger data-testid="manager-lang-switcher" className="h-8 text-xs border-slate-200 flex-1">
                  <SelectValue>{LANG_LABELS[language]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="vi">Tiếng Việt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="manager-logout" onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-600">
              <LogOut size={18} className="mr-3" />
              {t('logout')}
            </Button>
          </div>
        </aside>

        <main className="flex-1 md:ml-64 p-6 md:p-8">
          {/* Notification banner */}
          {notifBanner && (
            <div data-testid="new-order-notification" className="mb-4 animate-in slide-in-from-top">
              <div className="bg-emerald-600 text-white rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
                <Bell size={20} className="animate-bounce" />
                <span className="font-semibold">{notifBanner}</span>
                <button onClick={() => setNotifBanner(null)} className="ml-auto text-emerald-200 hover:text-white text-lg font-bold">&times;</button>
              </div>
            </div>
          )}
          {alertNotifs.length > 0 && (
            <div data-testid="color-alert-notification" className="mb-4 space-y-2">
              {alertNotifs.map(n => (
                <div key={n.id} className={`rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg ${n.msg.includes('red') || n.msg.includes('retard') || n.msg.includes('Urgent') || n.msg.includes('quá hạn') ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                  <Bell size={20} className="animate-bounce" />
                  <span className="font-semibold">{n.msg}</span>
                  <button onClick={() => setAlertNotifs(prev => prev.filter(x => x.id !== n.id))} className="ml-auto opacity-70 hover:opacity-100 text-lg font-bold">&times;</button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('manager_title')}</h1>
                <p className="text-sm text-slate-600 mt-1">{t('manager_subtitle')}</p>
                <StatusFlowBar />
              </div>

              <Card data-testid="manager-orders-card" className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">{t('orders_in_progress')} ({orders.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <p className="text-sm text-slate-500">{t('no_orders_in_progress')}</p>
                  ) : (
                    <div className="space-y-2">
                      {orders.map((order) => (
                        <div
                          key={order.order_id}
                          data-testid={`manager-alert-${order.color}`}
                          className={`flex items-center p-3 rounded-md border transition-colors ${getRowBgColor(order.color)}`}
                        >
                          {/* Order image thumbnail */}
                          <div className="mr-3 flex-shrink-0">
                            {order.images && order.images.length > 0 ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${order.images[0]}`}
                                alt=""
                                data-testid="order-thumbnail"
                                className="w-12 h-12 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openImagePopup(order.images)}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-slate-200 rounded border border-slate-300 flex items-center justify-center">
                                <ImageIcon size={16} className="text-slate-400" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900">
                              {order.order_number} - {order.client_name} {order.client_firstname}
                            </p>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              <Badge className={`text-xs border uppercase ${
                                order.status === 'commandée' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                order.status === 'impression' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                                order.status === 'couture' ? 'bg-indigo-100 text-indigo-800 border-indigo-200' :
                                order.status === 'livraison bureau' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                order.status === 'en cours de livraison client' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                'bg-slate-100 text-slate-800 border-slate-200'
                              }`}>
                                {translateStatus(order.status, t)}
                              </Badge>
                              <span className="text-xs text-slate-600">{order.company}</span>
                              <span className="text-xs text-slate-600">{order.client_country}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 ml-4">
                            {order.dropbox_link && (
                              <a href={order.dropbox_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                              </a>
                            )}
                            {order.bureau_delivery_date && (
                              <div className={`text-right px-3 py-1 rounded ${
                                order.color === 'green' ? 'bg-emerald-200 text-emerald-900' :
                                order.color === 'orange' ? 'bg-amber-200 text-amber-900' :
                                'bg-red-200 text-red-900'
                              }`}>
                                <p className="text-xs font-medium uppercase">{t('delivery_office')}</p>
                                <p className="text-sm font-bold">{new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR')}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'archives' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('archives')}</h1>
                <p className="text-sm text-slate-600 mt-1">{archives.length} {t('delivered_orders')}</p>
              </div>
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="pt-6">
                  {archives.length === 0 ? (
                    <p className="text-center py-8 text-sm text-slate-500">{t('no_orders_in_progress')}</p>
                  ) : (
                    <div className="space-y-3">
                      {groupByMonth(archives).map((group) => (
                        <div key={group.label} data-testid="manager-archive-month" className="border border-slate-200 rounded-lg overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                            onClick={() => toggleMonth(group.label)}
                          >
                            <div className="flex items-center gap-3">
                              {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                              <span className="font-semibold text-slate-900">{group.label}</span>
                            </div>
                            <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length}</Badge>
                          </button>
                          {expandedMonths[group.label] && (
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-white">
                                  <TableHead className="font-medium w-14"></TableHead>
                                  <TableHead className="font-medium">{t('order_number')}</TableHead>
                                  <TableHead className="font-medium">{t('client')}</TableHead>
                                  <TableHead className="font-medium">{t('country')}</TableHead>
                                  <TableHead className="font-medium">{t('status')}</TableHead>
                                  <TableHead className="font-medium">DROPBOX</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.orders.map((order) => (
                                  <TableRow key={order.order_id}>
                                    <TableCell>
                                      {order.images?.length > 0 ? (
                                        <img
                                          src={`${process.env.REACT_APP_BACKEND_URL}${order.images[0]}`}
                                          alt=""
                                          className="w-10 h-10 object-cover rounded cursor-pointer"
                                          onClick={() => openImagePopup(order.images)}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                          <ImageIcon size={14} className="text-slate-400" />
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="font-medium">{order.order_number}</TableCell>
                                    <TableCell>{order.client_name} {order.client_firstname}</TableCell>
                                    <TableCell>{order.client_country}</TableCell>
                                    <TableCell>
                                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs uppercase">{translateStatus(order.status, t)}</Badge>
                                    </TableCell>
                                    <TableCell>
                                      {order.dropbox_link ? (
                                        <a href={order.dropbox_link} target="_blank" rel="noreferrer">
                                          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                                        </a>
                                      ) : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
        <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
          <DialogHeader>
            <DialogTitle>{t('image_popup_title')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {imagePopupImages.map((img, i) => (
              <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full rounded-lg border border-slate-200" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
